import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { ArrowLeft, TrendingUp, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Productivity() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [period, setPeriod] = useState<"today" | "week" | "month" | "custom">("week" as "today" | "week" | "month" | "custom");

  // Redirecionar para login se não autenticado
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Calcular datas baseado no período selecionado
  useEffect(() => {
    const now = new Date();
    let start = new Date();
    
    switch (period) {
      case "today":
        start.setHours(0, 0, 0, 0);
        break;
      case "week":
        start.setDate(now.getDate() - 7);
        break;
      case "month":
        start.setDate(now.getDate() - 30);
        break;
      case "custom":
        return; // Não atualizar automaticamente no modo custom
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(now.toISOString().split('T')[0]);
  }, [period]);

  // Query de produtividade por operador
  const { data: productivity = [] } = trpc.productivity.byOperator.useQuery(
    {
      startDate: startDate ? new Date(startDate) : undefined,
      endDate: endDate ? new Date(endDate) : undefined,
    },
    { 
      enabled: !!user && (user.role === "lider" || user.role === "admin"),
    }
  );

  const handleExportExcel = async () => {
    try {
      const XLSX = await import("xlsx");
      
      const headers = [
        "Responsável",
        "Total",
        "Concluídos",
        "Em Andamento",
        "Abertos",
        "Cancelados",
        "Tempo Médio (min)",
        "Taxa de Conclusão (%)",
      ];
      
      const rows = productivity.map((p) => {
        const taxaConclusao = p.total > 0 ? ((Number(p.concluidos) / Number(p.total)) * 100).toFixed(1) : "0";
        const avgMinutes = p.avgMinutes ? Number(p.avgMinutes).toFixed(1) : "-";
        
        return [
          p.responsavel,
          p.total,
          p.concluidos,
          p.emAndamento,
          p.abertos,
          p.cancelados,
          avgMinutes,
          taxaConclusao,
        ];
      });
      
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Produtividade");
      XLSX.writeFile(workbook, `produtividade_${startDate}_${endDate}.xlsx`);
    } catch (error) {
      console.error("Erro ao exportar:", error);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-card border-b shadow-sm">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-foreground">
              Produtividade por Operador
            </h1>
            <Button
              onClick={handleExportExcel}
              size="sm"
              variant="outline"
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Exportar Excel
            </Button>
          </div>
        </div>
      </div>

      <div className="container py-6">
        {/* Filtros de Período */}
        <div className="bg-card rounded-lg border shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Filtros de Período</h2>
          
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              variant={period === "today" ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod("today")}
            >
              Hoje
            </Button>
            <Button
              variant={period === "week" ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod("week")}
            >
              Últimos 7 dias
            </Button>
            <Button
              variant={period === "month" ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod("month")}
            >
              Últimos 30 dias
            </Button>
            <Button
              variant={period === "custom" ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod("custom")}
            >
              Personalizado
            </Button>
          </div>

          {period === "custom" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Data Inicial</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Data Final</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Tabela de Produtividade */}
        <div className="bg-card rounded-lg border shadow-sm">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Métricas por Operador</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                      Responsável
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">
                      Total
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">
                      Concluídos
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">
                      Em Andamento
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">
                      Abertos
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">
                      Cancelados
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">
                      Tempo Médio
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">
                      Taxa de Conclusão
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {productivity.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-muted-foreground">
                        Nenhum dado disponível para o período selecionado
                      </td>
                    </tr>
                  ) : (
                    productivity.map((p, index) => {
                      const total = Number(p.total);
                      const concluidos = Number(p.concluidos);
                      const taxaConclusao = total > 0 ? ((concluidos / total) * 100).toFixed(1) : "0";
                      const avgMinutes = p.avgMinutes ? Number(p.avgMinutes) : null;
                      
                      return (
                        <tr key={index} className="hover:bg-muted/30">
                          <td className="py-3 px-4 text-sm font-medium">
                            {p.responsavel}
                          </td>
                          <td className="py-3 px-4 text-sm text-center">
                            {p.total}
                          </td>
                          <td className="py-3 px-4 text-sm text-center">
                            <span className="text-green-600 font-medium">{p.concluidos}</span>
                          </td>
                          <td className="py-3 px-4 text-sm text-center">
                            <span className="text-blue-600">{p.emAndamento}</span>
                          </td>
                          <td className="py-3 px-4 text-sm text-center">
                            <span className="text-orange-600">{p.abertos}</span>
                          </td>
                          <td className="py-3 px-4 text-sm text-center">
                            <span className="text-red-600">{p.cancelados}</span>
                          </td>
                          <td className="py-3 px-4 text-sm text-center">
                            {avgMinutes !== null ? (
                              <span>
                                {avgMinutes >= 60 
                                  ? `${Math.floor(avgMinutes / 60)}h ${Math.round(avgMinutes % 60)}min`
                                  : `${Math.round(avgMinutes)}min`
                                }
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-24 bg-muted rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${
                                    Number(taxaConclusao) >= 80
                                      ? "bg-green-500"
                                      : Number(taxaConclusao) >= 50
                                      ? "bg-yellow-500"
                                      : "bg-red-500"
                                  }`}
                                  style={{ width: `${taxaConclusao}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium w-12 text-right">
                                {taxaConclusao}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
